import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Box, Flex } from 'reflexbox';

import { Callout, Checkbox, Modal, Paragraph, Radio, SegmentedControl } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import UserSelector from './UserSelector';

export default function CreateUserRole(props: {
	inverse?: boolean;
	user: graphql.User;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	refetch: () => Promise<any>;
	onCancel: () => void;
	onCreate: () => void;
}) {
	const { t } = useTranslation();

	const [user, setUser] = useState<graphql.User>();

	const source = props.inverse ? user : props.user;
	const target = props.inverse ? props.user : user;

	const [type, setType] = useState<'manager' | 'coach'>('manager');
	const [role, setRole] = useState<graphql.RoleType>(graphql.RoleType.PrimaryManager);
	const [transitive, setTransitive] = useState(false);

	const [create] = graphql.useAssignRoleMutation();

	function toggleCoachRole() {
		if (role === graphql.RoleType.Coach) {
			setRole(graphql.RoleType.Buddy);
		} else {
			setRole(graphql.RoleType.Coach);
		}
	}

	const roleIDs: Partial<Record<graphql.RoleType, string>> = {
		[graphql.RoleType.Buddy]: 'management-buddy',
		[graphql.RoleType.Coach]: 'management-coach',
		[graphql.RoleType.CollaborativeManager]: 'management-collaborative-manager',
		[graphql.RoleType.PrimaryManager]: 'management-primary-manager',
	};

	return source && target ? (
		<Modal width={400}>
			<Modal.Contents>
				<Paragraph mb={3}>{t('settings.employees.roles.create.user.instruction')}:</Paragraph>
				<Flex
					mb={3}
					onClick={() => {
						if (type !== 'manager') {
							setType('manager');
							setRole(graphql.RoleType.PrimaryManager);
						}
					}}
				>
					<Box flexGrow={0} flexShrink={0} mr={2}>
						<Radio
							checked={type === 'manager'}
							onChange={() => {
								setType('manager');
								setRole(graphql.RoleType.PrimaryManager);
							}}
						/>
					</Box>
					<Box>
						<Paragraph color="slateBlue">
							{t('settings.employees.roles.create.explanations.manager.general', {
								source: `${source.firstName} ${source.lastName}`,
								target: `${target.firstName} ${target.lastName}`,
							})}
						</Paragraph>
						{type === 'manager' && (
							<Box mt={2}>
								<SegmentedControl
									options={[
										{
											title: t('roles.management-primary-manager'),
											value: graphql.RoleType.PrimaryManager,
										},
										{
											title: t('roles.management-collaborative-manager'),
											value: graphql.RoleType.CollaborativeManager,
										},
									]}
									value={role!}
									onChange={(role) => setRole(role as graphql.RoleType)}
								/>
								{role === graphql.RoleType.CollaborativeManager && (
									<Box mt={2}>
										<Callout color="employee">
											{t('settings.employees.roles.create.explanations.manager.collaborative', {
												source: source.firstName,
												target: target.firstName,
											})}
										</Callout>
									</Box>
								)}
							</Box>
						)}
					</Box>
				</Flex>
				<Flex
					onClick={() => {
						if (type !== 'coach') {
							setType('coach');
							setRole(graphql.RoleType.Coach);
						}
					}}
				>
					<Box flexGrow={0} flexShrink={0} mr={2}>
						<Radio
							checked={type === 'coach'}
							onChange={() => {
								setType('coach');
								setRole(graphql.RoleType.Coach);
							}}
						/>
					</Box>
					<Box>
						<Paragraph color="slateBlue">
							{t('settings.employees.roles.create.explanations.coach.general', {
								source: `${source.firstName} ${source.lastName}`,
								target: `${target.firstName} ${target.lastName}`,
							})}
						</Paragraph>
						{type === 'coach' && (
							<Flex mt={2} onClick={toggleCoachRole}>
								<Box flexGrow={0} flexShrink={0} mr={2}>
									<Checkbox checked={role === graphql.RoleType.Coach} />
								</Box>
								<Box>
									<Paragraph color="slateBlue">
										{t('settings.employees.roles.create.explanations.coach.buddy', {
											source: source.firstName,
											target: target.firstName,
										})}
									</Paragraph>
								</Box>
							</Flex>
						)}
					</Box>
				</Flex>
				<Flex mt={4} onClick={() => setTransitive((transitive) => !transitive)}>
					<Box flexGrow={0} flexShrink={0} mr={2}>
						<Checkbox checked={transitive} />
					</Box>
					<Box>
						<Paragraph color="slateBlue">
							{t('settings.employees.roles.create.explanations.transitive', {
								source: source.firstName,
								target: target.firstName,
							})}
						</Paragraph>
					</Box>
				</Flex>
			</Modal.Contents>
			<Modal.FormFooter
				small
				onCancel={props.onCancel}
				onSave={async () => {
					await create({
						variables: {
							input: {
								targetID: target.id,
								targetType: graphql.RoleTargetType.User,
								isIndirect: transitive,
								roleID: roleIDs[role!]!,
								userID: source.id,
							},
						},
					});
					await props.refetch();
					props.onCreate();
				}}
				disabled={role === undefined}
			/>
		</Modal>
	) : (
		<UserSelector
			me={props.user}
			placeholder={t('settings.employees.roles.create.user.placeholder', {
				name: props.user.firstName,
			})}
			onChange={(user) => setUser(user as graphql.User)}
		/>
	);
}
